import React, { useState, useEffect } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Plus,
  BarChart3,
  HelpCircle,
  FileText,
  Upload,
  
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useHelp } from '../../context/HelpContext';
import PageHelp from '../help/PageHelp';
import ContractService from '../../services/contractService';
import AIService from '../../services/aiService';
import { useToast, ToastContainer } from '../common/Toast';

const ComplianceTracking = () => {
  const { openPageHelp } = useHelp();
  const { success, error: showError, toasts, removeToast } = useToast();
  const [activeTab, setActiveTab] = useState('active');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompliance, setSelectedCompliance] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [complianceData, setComplianceData] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiInsights, setAiInsights] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formData, setFormData] = useState({
    contractId: '',
    complianceType: '',
    complianceCategory: '',
    obligationDescription: '',
    regulatoryReference: '',
    complianceStatus: 'pending',
    dueDate: '',
    evidenceNotes: '',
    riskLevel: 'medium',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [evidenceFile, setEvidenceFile] = useState(null);

  const transformComplianceRecord = (record, contractLookup = {}) => {
    const contract =
      record.contractId && typeof record.contractId === 'object'
        ? record.contractId
        : contractLookup[record.contractId] || {};
    const recordContractId =
      typeof record.contractId === 'object'
        ? record.contractId?._id
        : record.contractId;
    const contractIdentifier =
      contract.contractId || contract._id || recordContractId || '';
    const normalizedContractId = contractIdentifier
      ? String(contractIdentifier)
      : '';
    const contractTitle = contract.title || 'Untitled Contract';
    const contractStatus = contract.status || '';
    const dueDate = record.dueDate ? new Date(record.dueDate) : null;
    const isOverdue =
      record.isOverdue || (dueDate ? dueDate < new Date() : false);

    return {
      id: record._id || record.id,
      contractId: normalizedContractId,
      contractTitle,
      contractStatus,
      complianceType: record.complianceType,
      complianceCategory: record.complianceCategory,
      obligationDescription: record.obligationDescription,
      regulatoryReference: record.regulatoryReference || '',
      complianceStatus: record.complianceStatus,
      dueDate: record.dueDate,
      evidenceNotes: record.evidenceNotes || '',
      evidence: record.evidence || [],
      evidenceSubmittedAt: record.evidenceSubmittedAt || null,
      riskLevel: record.riskLevel,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      isOverdue,
      escalationStatus: record.escalationStatus || 'none',
      escalatedAt: record.escalatedAt || null,
      isReadOnly: contractStatus === 'Terminated' || contract.isActive === false
    };
  };

  const loadContracts = async () => {
    const response = await ContractService.getContracts({
      includeInactive: true,
    });
    const contractList = response.data || response || [];
    setContracts(contractList);
    return contractList;
  };

  // Load compliance data from backend
  const loadComplianceData = async () => {
    setLoading(true);
    try {
      const contractList = await loadContracts();
      if (!Array.isArray(contractList) || contractList.length === 0) {
        setComplianceData([]);
        return;
      }

      const contractLookup = contractList.reduce((acc, contract) => {
        const key = contract.contractId || contract.id;
        if (key) {
          acc[key] = contract;
        }
        return acc;
      }, {});

      const complianceResponses = await Promise.all(
        contractList.map(async (contract) => {
          const identifier = contract.contractId || contract.id;
          if (!identifier) return [];
          try {
            const result = await ContractService.getContractCompliance(
              identifier
            );
            return result?.data || [];
          } catch (error) {
            console.error(
              'Error loading compliance for contract',
              identifier,
              error
            );
            return [];
          }
        })
      );

      const flattened = complianceResponses.flat();
      const transformedData = flattened.map((record) =>
        transformComplianceRecord(record, contractLookup)
      );
      setComplianceData(transformedData);

      if (activeTab === 'analytics') {
        try {
          const aiResponse = await AIService.generateInsights(
            'compliance',
            '30d',
            {
              totalCompliance: transformedData.length,
              compliant: transformedData.filter(
                (c) => c.complianceStatus === 'compliant'
              ).length,
              nonCompliant: transformedData.filter(
                (c) => c.complianceStatus === 'non-compliant'
              ).length,
              overdue: transformedData.filter((c) => c.isOverdue).length,
            }
          );
          setAiInsights(aiResponse);
        } catch (aiError) {
          console.error('Error generating AI insights:', aiError);
        }
      }
    } catch (error) {
      console.error('Error loading compliance data:', error);
      showError('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComplianceData();
  }, [activeTab]);

  // Calculate statistics
  const stats = {
    totalCompliance: complianceData.length,
    compliant: complianceData.filter((c) => c.complianceStatus === 'compliant')
      .length,
    nonCompliant: complianceData.filter(
      (c) => c.complianceStatus === 'non-compliant'
    ).length,
    pending: complianceData.filter((c) => c.complianceStatus === 'pending')
      .length,
    overdue: complianceData.filter((c) => c.isOverdue).length,
    averageComplianceScore:
      complianceData.length > 0
        ? Math.round(
            (complianceData.filter((c) => c.complianceStatus === 'compliant')
              .length /
              complianceData.length) *
              100
          )
        : 0,
  };

  // Filter compliance by active tab
  const getFilteredCompliance = () => {
    let filtered = complianceData;

    switch (activeTab) {
      case 'active':
        filtered = complianceData.filter(
          (c) => c.complianceStatus === 'compliant'
        );
        break;
      case 'non-compliant':
        filtered = complianceData.filter(
          (c) => c.complianceStatus === 'non-compliant'
        );
        break;
      case 'pending':
        filtered = complianceData.filter(
          (c) => c.complianceStatus === 'pending'
        );
        break;
      case 'all':
        filtered = complianceData;
        break;
      default:
        filtered = complianceData;
    }

    return filtered;
  };

  const handleRefresh = () => {
    loadComplianceData();
    success('Compliance data refreshed');
  };

  const handleAddNew = () => {
    if (contracts.length === 0) {
      showError('Create a contract before adding compliance records');
      return;
    }
    setSelectedCompliance(null);
    setFormMode('create');
    setFormData({
      contractId: '',
      complianceType: '',
      complianceCategory: '',
      obligationDescription: '',
      regulatoryReference: '',
      complianceStatus: 'pending',
      dueDate: '',
      evidenceNotes: '',
      riskLevel: 'medium',
    });
    setShowFormModal(true);
  };

  // Header actions
  const headerActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh compliance',
      disabled: loading,
    },
    {
      label: 'New Compliance',
      icon: <Plus size={18} />,
      onClick: handleAddNew,
      variant: 'primary',
    },
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: () => openPageHelp('compliance-tracking'),
      variant: 'outline',
      tooltip: 'Help',
    },
  ];

  // KPI card configurations matching Dashboard
  const kpiCards = [
    {
      value: stats.totalCompliance,
      label: 'Total Compliance',
      icon: Shield,
      iconColor: '#2D7DD2',
      iconBg: '#D6EFFF',
    },
    {
      value: stats.compliant,
      label: 'Compliant',
      icon: CheckCircle,
      iconColor: '#059669',
      iconBg: '#D1FAE5',
    },
    {
      value: stats.nonCompliant,
      label: 'Non-Compliant',
      icon: XCircle,
      iconColor: '#EF4444',
      iconBg: '#FEE2E2',
    },
    {
      value: stats.pending,
      label: 'Pending Review',
      icon: Clock,
      iconColor: '#F59E0B',
      iconBg: '#FEF3C7',
    },
    {
      value: stats.overdue,
      label: 'Overdue Items',
      icon: AlertTriangle,
      iconColor: '#EF4444',
      iconBg: '#FEE2E2',
    },
    {
      value: `${Math.round(stats.averageComplianceScore)}%`,
      label: 'Avg. Compliance',
      icon: BarChart3,
      iconColor: '#7C3AED',
      iconBg: '#EDE9FE',
    },
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'contractTitle',
      label: 'Contract',
      width: '240px',
      render: (value, row) => (
        <div>
          <div className="fw-medium">{value}</div>
          <div className="text-muted" style={{ fontSize: '12px' }}>
            {row.contractId}
          </div>
        </div>
      ),
    },
    {
      key: 'contractStatus',
      label: 'Contract Status',
      width: '140px',
      render: (status) => (
        <span
          className={`badge ${
            status === 'Terminated' ? 'badge-danger' : 'badge-success'
          }`}
        >
          {status || 'Unknown'}
        </span>
      ),
    },
    {
      key: 'complianceType',
      label: 'Type',
      width: '160px',
    },
    {
      key: 'complianceCategory',
      label: 'Category',
      width: '160px',
    },
    {
      key: 'complianceStatus',
      label: 'Status',
      width: '140px',
      render: (status) => {
        const statusConfig = {
          compliant: {
            className: 'badge-success',
            icon: <CheckCircle size={14} />,
          },
          'non-compliant': {
            className: 'badge-danger',
            icon: <XCircle size={14} />,
          },
          pending: { className: 'badge-warning', icon: <Clock size={14} /> },
        };
        const config = statusConfig[status] || statusConfig.pending;
        const label =
          status === 'non-compliant'
            ? 'Non-Compliant'
            : status === 'compliant'
            ? 'Compliant'
            : 'Pending';

        return (
          <span
            className={`badge ${config.className} d-flex align-items-center gap-1`}
          >
            {config.icon}
            {label}
          </span>
        );
      },
    },
    {
      key: 'riskLevel',
      label: 'Risk',
      width: '110px',
      render: (risk) => {
        const riskConfig = {
          high: 'badge-danger',
          medium: 'badge-warning',
          low: 'badge-success',
        };
        const label = risk
          ? risk.charAt(0).toUpperCase() + risk.slice(1)
          : 'Medium';
        return (
          <span className={`badge ${riskConfig[risk] || 'badge-warning'}`}>
            {label}
          </span>
        );
      },
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      width: '140px',
      render: (value, row) => {
        if (!value) return '—';
        const dueDate = new Date(value);
        return (
          <div
            style={{
              fontSize: '12px',
              color: row.isOverdue ? '#ef4444' : '#374151',
              fontWeight: row.isOverdue ? '500' : '400',
            }}
          >
            {dueDate.toLocaleDateString()}
            {row.isOverdue && <div style={{ fontSize: '10px' }}>Overdue</div>}
            {row.escalationStatus === 'escalated' && <div style={{ fontSize: '10px' }}>Escalated</div>}
          </div>
        );
      },
    },
  ];

  // Handler functions
  const handleView = (compliance) => {
    setSelectedCompliance(compliance);
    setFormMode('view');
    setFormData({
      contractId: compliance.contractId || '',
      complianceType: compliance.complianceType || '',
      complianceCategory: compliance.complianceCategory || '',
      obligationDescription: compliance.obligationDescription || '',
      regulatoryReference: compliance.regulatoryReference || '',
      complianceStatus: compliance.complianceStatus || 'pending',
      dueDate: compliance.dueDate
        ? new Date(compliance.dueDate).toISOString().split('T')[0]
        : '',
      evidenceNotes: compliance.evidenceNotes || '',
      riskLevel: compliance.riskLevel || 'medium',
    });
    setShowFormModal(true);
  };

  const handleEdit = (compliance) => {
    if (compliance.isReadOnly) {
      showError('Compliance records are read-only for terminated contracts');
      return;
    }
    setSelectedCompliance(compliance);
    setFormMode('edit');
    setFormData({
      contractId: compliance.contractId || '',
      complianceType: compliance.complianceType || '',
      complianceCategory: compliance.complianceCategory || '',
      obligationDescription: compliance.obligationDescription || '',
      regulatoryReference: compliance.regulatoryReference || '',
      complianceStatus: compliance.complianceStatus || 'pending',
      dueDate: compliance.dueDate
        ? new Date(compliance.dueDate).toISOString().split('T')[0]
        : '',
      evidenceNotes: compliance.evidenceNotes || '',
      riskLevel: compliance.riskLevel || 'medium',
    });
    setShowFormModal(true);
  };

  const handleDelete = (compliance) => {
    setSelectedCompliance(compliance);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (selectedCompliance?.isReadOnly) {
        showError('Compliance records are read-only for terminated contracts');
        return;
      }
      if (selectedCompliance?.contractId && selectedCompliance?.id) {
        await ContractService.deleteContractCompliance(
          selectedCompliance.contractId,
          selectedCompliance.id
        );
        success('Compliance record deleted successfully');
        await loadComplianceData();
      }
      setShowDeleteModal(false);
      setSelectedCompliance(null);
    } catch (error) {
      console.error('Error deleting compliance:', error);
      showError('Failed to delete compliance record');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const requiredFields = [
      'contractId',
      'complianceType',
      'complianceCategory',
      'obligationDescription',
      'complianceStatus',
      'dueDate',
      'riskLevel',
    ];
    const missing = requiredFields.filter((field) => !formData[field]);
    if (missing.length > 0) {
      showError(`Missing required fields: ${missing.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (formMode === 'view') {
      return;
    }
    const selectedContract = contracts.find(
      (contract) => (contract.contractId || contract.id) === formData.contractId
    );
    const isReadOnly =
      selectedContract?.status === 'Terminated' ||
      selectedContract?.isActive === false;

    if (isReadOnly) {
      showError('Compliance records are read-only for terminated contracts');
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (selectedCompliance?.evidenceSubmittedAt && !String(formData.evidenceNotes || '').trim()) {
      showError('Evidence cannot be removed once submitted');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        complianceType: formData.complianceType,
        complianceCategory: formData.complianceCategory,
        obligationDescription: formData.obligationDescription,
        regulatoryReference: formData.regulatoryReference,
        complianceStatus: formData.complianceStatus,
        dueDate: formData.dueDate,
        evidenceNotes: formData.evidenceNotes,
        riskLevel: formData.riskLevel,
      };

      if (formMode === 'edit' && selectedCompliance?.id) {
        await ContractService.updateContractCompliance(
          formData.contractId,
          selectedCompliance.id,
          payload
        );
        success('Compliance record updated successfully');
      } else if (formMode === 'create') {
        await ContractService.createContractCompliance(
          formData.contractId,
          payload
        );
        success('Compliance record created successfully');
      }

      setShowFormModal(false);
      setSelectedCompliance(null);
      await loadComplianceData();
    } catch (error) {
      console.error('Error saving compliance record:', error);
      const message =
        error?.response?.data?.message || 'Failed to save compliance record';
      showError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'all', label: 'All Compliance', icon: Shield },
    { id: 'active', label: 'Active Compliance', icon: CheckCircle },
    { id: 'non-compliant', label: 'Non-Compliant', icon: XCircle },
    { id: 'pending', label: 'Pending Review', icon: Clock },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  // Chart colors
  const COLORS = [
    '#4678be',
    '#059669',
    '#F59E0B',
    '#EF4444',
    '#7C3AED',
    '#06B6D4',
  ];

  // Render Analytics Tab
  const renderAnalytics = () => {
    const statusData = [
      { name: 'Compliant', value: stats.compliant },
      { name: 'Non-Compliant', value: stats.nonCompliant },
      { name: 'Pending', value: stats.pending },
    ];

    const typeMap = complianceData.reduce((acc, item) => {
      const type = item.complianceType || 'Unspecified';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    const typeData = Object.entries(typeMap).map(([name, value]) => ({
      name,
      value,
    }));

    // Calculate compliance trends (last 6 months)
    const complianceTrends = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      const monthCompliant = complianceData.filter((c) => {
        if (!c.createdAt) return false;
        const createdAt = new Date(c.createdAt);
        return (
          createdAt.getMonth() === date.getMonth() &&
          createdAt.getFullYear() === date.getFullYear() &&
          c.complianceStatus === 'compliant'
        );
      }).length;
      complianceTrends.push({ month: monthName, compliant: monthCompliant });
    }

    return (
      <div style={{ marginTop: '32px' }}>
        {/* AI Insights Card */}
        {aiInsights && (
          <div
            style={{
              backgroundColor: '#EDE9FE',
              border: '1px solid #7C3AED',
              borderRadius: '0',
              padding: '20px',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <BarChart3 size={24} style={{ color: '#7C3AED' }} />
              <h5
                style={{
                  margin: 0,
                  color: '#7C3AED',
                  fontFamily: 'var(--font-family)',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                AI-Powered Insights
              </h5>
            </div>
            <div
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '14px',
                lineHeight: '1.6',
              }}
            >
              {aiInsights.insights ||
                aiInsights.summary ||
                'Analyzing compliance trends and patterns...'}
            </div>
          </div>
        )}

        {/* Charts Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '24px',
          }}
        >
          {/* Status Distribution */}
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Compliance by Status
            </h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Type Distribution */}
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Compliance by Type
            </h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={typeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#4678be"
                    name="Compliance"
                    radius={[0, 0, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Compliance Trends */}
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            borderRadius: '0',
            padding: '24px',
          }}
        >
          <h5
            style={{
              marginBottom: '20px',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family)',
              fontSize: '18px',
              fontWeight: '600',
            }}
          >
            Compliance Trends (Last 6 Months)
          </h5>
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={complianceTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="month"
                  stroke="#6b7280"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0',
                    fontFamily: 'var(--font-family)',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="compliant"
                  stroke="#4678be"
                  strokeWidth={2}
                  name="Compliant"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const selectedContract = contracts.find(
    (contract) => (contract.contractId || contract.id) === formData.contractId
  );
  const isReadOnlyContract =
    selectedContract?.status === 'Terminated' ||
    selectedContract?.isActive === false;
  const formTitle =
    formMode === 'edit'
      ? 'Edit Compliance Record'
      : formMode === 'view'
      ? 'Compliance Details'
      : 'New Compliance Record';

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Compliance Tracking"
        description="Monitor regulatory compliance, track violations, and manage corrective actions"
        actions={headerActions}
      />

      {/* KPI Cards – StatCard visual language (summary-style, visual only) */}
      <div className="m-8">
        <div className="grid-modern-4 w-full gap-6">
          {kpiCards.map((card, index) => {
            const IconComponent = card.icon;

            return (
              <div
                key={index}
                className="relative border border-[var(--border-color)] bg-white"
              >
                {/* Top Accent Line */}
                <div className="h-1 w-full bg-[var(--primary-color)]" />

                {/* Content */}
                <div className="flex items-center gap-3 p-4">
                  {/* Icon (kept compact like summary cards) */}
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center"
                    style={{ background: card.iconBg }}
                  >
                    <IconComponent
                      size={20}
                      style={{ color: card.iconColor }}
                    />
                  </div>

                  {/* Text */}
                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {card.value}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      {card.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        style={{
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* Tabbed Navigation */}
        <div className="m-8 mt-0">
          <div className="mb-8 border border-slate-200">
            <div className="flex items-center w-full bg-white border-b border-slate-200">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`
            flex items-center justify-center gap-2 px-6 py-3 w-full
            text-sm font-medium
            border-b-2 transition-colors duration-200
            ${
              isActive
                ? 'border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                : 'border-transparent text-[var(--text-secondary)] hover:bg-slate-100 hover:text-slate-800'
            }
          `}
                  >
                    <IconComponent size={18} />
                    <span className="whitespace-nowrap">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content - DataTable */}
          {(activeTab === 'active' ||
            activeTab === 'non-compliant' ||
            activeTab === 'pending' ||
            activeTab === 'all') && (
            <div className="page-tab-content" style={{ marginTop: '32px' }}>
              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    flexDirection: 'column',
                    gap: '16px',
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                    padding: '40px',
                  }}
                >
                  <div className="loading-spinner"></div>
                  <p
                    style={{
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Loading compliance data...
                  </p>
                </div>
              ) : (
                <div
                  className="page-table-container"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <DataTable
                    data={getFilteredCompliance()}
                    columns={columns}
                    title="Compliance Tracking"
                    searchable={true}
                    sortable={true}
                    exportable={true}
                    pagination={true}
                    pageSize={10}
                    showActions={true}
                    showCheckboxes={false}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    searchPlaceholder="Search compliance by contract, type, or status..."
                    emptyMessage="No compliance records found. Click 'New Compliance' to get started."
                    initialHiddenColumns={['id']}
                  />
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && renderAnalytics()}
        </div>

        {showFormModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1050,
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid var(--border-color)',
                width: '100%',
                maxWidth: '720px',
                maxHeight: '90vh',
                overflowY: 'auto',
                padding: '24px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <h4 style={{ margin: 0 }}>{formTitle}</h4>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setShowFormModal(false)}
                >
                  Close
                </button>
              </div>

              {isReadOnlyContract && formMode !== 'view' && (
                <div
                  style={{
                    backgroundColor: '#FEE2E2',
                    padding: '10px 12px',
                    marginBottom: '16px',
                    color: '#991B1B',
                  }}
                >
                  Compliance records are read-only for terminated contracts.
                </div>
              )}

              <form onSubmit={handleFormSubmit}>
                <div className="mb-3">
                  <label className="form-label">Contract</label>
                  <select
                    className="form-select"
                    value={formData.contractId}
                    onChange={(event) =>
                      handleFormChange('contractId', event.target.value)
                    }
                    disabled={formMode !== 'create'}
                  >
                    <option value="">Select a contract</option>
                    {contracts.map((contract) => {
                      const id = contract.contractId || contract.id;
                      return (
                        <option key={id} value={id}>
                          {contract.title} ({id})
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Compliance Type</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.complianceType}
                    onChange={(event) =>
                      handleFormChange('complianceType', event.target.value)
                    }
                    disabled={formMode === 'view'}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Compliance Category</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.complianceCategory}
                    onChange={(event) =>
                      handleFormChange('complianceCategory', event.target.value)
                    }
                    disabled={formMode === 'view'}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Obligation Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.obligationDescription}
                    onChange={(event) =>
                      handleFormChange(
                        'obligationDescription',
                        event.target.value
                      )
                    }
                    disabled={formMode === 'view'}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Regulatory Reference</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.regulatoryReference}
                    onChange={(event) =>
                      handleFormChange(
                        'regulatoryReference',
                        event.target.value
                      )
                    }
                    disabled={formMode === 'view'}
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Compliance Status</label>
                    <select
                      className="form-select"
                      value={formData.complianceStatus}
                      onChange={(event) =>
                        handleFormChange('complianceStatus', event.target.value)
                      }
                      disabled={formMode === 'view'}
                    >
                      <option value="pending">Pending</option>
                      <option value="compliant">Compliant</option>
                      <option value="non-compliant">Non-Compliant</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Risk Level</label>
                    <select
                      className="form-select"
                      value={formData.riskLevel}
                      onChange={(event) =>
                        handleFormChange('riskLevel', event.target.value)
                      }
                      disabled={formMode === 'view'}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Due Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.dueDate}
                    onChange={(event) =>
                      handleFormChange('dueDate', event.target.value)
                    }
                    disabled={formMode === 'view'}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Evidence / Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.evidenceNotes}
                    onChange={(event) =>
                      handleFormChange('evidenceNotes', event.target.value)
                    }
                    disabled={formMode === 'view'}
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label" style={{ fontWeight: '600' }}>Evidence History</label>
                  {selectedCompliance && selectedCompliance.evidence && selectedCompliance.evidence.length > 0 ? (
                    <div className="d-flex flex-column gap-2">
                      {selectedCompliance.evidence.map((file, index) => (
                        <div key={index} className="p-2 border rounded bg-light d-flex align-items-center">
                          <FileText size={16} className="text-primary me-2" />
                          <div>
                            <div className="small fw-bold">{file.fileName || "Unknown File"}</div>
                            <div className="text-muted" style={{ fontSize: '11px' }}>
                              {new Date(file.uploadDate).toLocaleDateString()} • Attested by: {file.attestedBy?.firstName || 'User'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted small fst-italic mt-1">No evidence files uploaded yet.</div>
                  )}
                </div>

                {formMode !== 'view' && (
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: '600', color: '#111827' }}>
                      Attach New Evidence (Optional)
                    </label>
                    <div
                      style={{
                        marginTop: '4px',
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '20px 24px',
                        borderWidth: '2px',
                        borderStyle: 'dashed',
                        borderColor: '#d1d5db',
                        borderRadius: '6px',
                        backgroundColor: '#f9fafb',
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <Upload
                          style={{
                            margin: '0 auto',
                            height: '48px',
                            width: '48px',
                            color: '#9ca3af',
                          }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            fontSize: '14px',
                            color: '#4b5563',
                            justifyContent: 'center',
                            marginTop: '8px',
                          }}
                        >
                          <label
                            htmlFor="file-upload"
                            style={{
                              position: 'relative',
                              cursor: 'pointer',
                              backgroundColor: 'white',
                              borderRadius: '4px',
                              fontWeight: 500,
                              color: '#2563eb',
                              padding: '0 4px',
                            }}
                          >
                            <span>Upload a file</span>
                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              style={{
                                position: 'absolute',
                                width: '1px',
                                height: '1px',
                                padding: 0,
                                margin: '-1px',
                                overflow: 'hidden',
                                clip: 'rect(0, 0, 0, 0)',
                                whiteSpace: 'nowrap',
                                borderWidth: 0
                              }}
                              onChange={(e) => setEvidenceFile(e.target.files[0])}
                            />
                          </label>
                        </div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          PDF, PNG, JPG up to 10MB
                        </p>
                      </div>
                    </div>
                    {evidenceFile && (
                      <div
                        style={{
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: '#eff6ff',
                          color: '#1d4ed8',
                          fontSize: '14px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          border: '1px solid #dbeafe',
                        }}
                      >
                        <FileText size={16} style={{ marginRight: '8px' }} />
                        Selected: {evidenceFile.name}
                      </div>
                    )}
                  </div>
                )}

                {formMode !== 'view' && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '12px',
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowFormModal(false)}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSaving || isReadOnlyContract}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Compliance Record"
          message={`Are you sure you want to delete the compliance record for "${
            selectedCompliance?.contractTitle || 'this contract'
          }"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        <PageHelp />
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default ComplianceTracking;
